import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "./firebase";

function Login({ setToken, setIsLoggedOut }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState('');
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);
  const navigate = useNavigate(); // Use navigate to redirect

  // Registration state
  const [regFirstName, setRegFirstName] = useState('');
  const [regLastName, setRegLastName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regMessage, setRegMessage] = useState('');
  const [regMessageType, setRegMessageType] = useState(''); // 'success' or 'error'
  const [showRegistration, setShowRegistration] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);

  //function to handle login
  const handleLogin = async () => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Get Firebase token
      const token = await userCredential.user.getIdToken();
      const refreshToken = userCredential.user.refreshToken;
      const userId = userCredential.user.uid;
      const firstName = userCredential.user.displayName || "User";
      const emailName = userCredential.user.email;

      // Store the token in state and localStorage
      setToken(token);

      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('userId', userId);
      localStorage.setItem('firstName', firstName);
      localStorage.setItem('email', emailName);
      
      // Redirect to dashboard after successful login
      localStorage.setItem('isEthosUser', 'true');
      window.location.href = "/scenarios";
    } catch (error) {
      setError('Login failed. Please check your credentials.');
      console.error("Error during login:", error);
    }
  };

  //functino to handle registration
  const handleRegister = async () => {
    setIsRegistering(true);
    setRegMessage('');
    setRegMessageType('');
  
    if (!regFirstName || !regLastName || !regEmail) {
      setRegMessage('Please fill in all fields.');
      setRegMessageType('error');
      setIsRegistering(false);
      return;
    }
  
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/ethos/register`, {
        firstName: regFirstName,
        lastName: regLastName,
        email: regEmail,
      });
  
      if (response.data.success) {
        setRegMessage('Registration successful! Please check your email to set your password.');
        setRegMessageType('success');
      } else {
        setRegMessage(response.data.message || 'Registration failed. Please try again.');
        setRegMessageType('error');
      }
    } catch (error) {
      console.error("Registration error:", error);
      setRegMessage(error.response?.data?.message || 'Registration failed. Please try again.');
      setRegMessageType('error');
    } finally {
      setIsRegistering(false);
    }
  };

  
  const eyeIcons = {
    open: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ width: "20px", height: "20px" }}
      >
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" fill="#666" />
      </svg>
    ),
    closed: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ width: "20px", height: "20px" }}
      >
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" fill="#666" />
        <line x1="4" y1="4" x2="20" y2="20" />
      </svg>
    ),
  };

  return (
    <div style={styles.container}>

      {/* Logos and Welcome Section */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '150px' }}>
        {/* Ethos Logo */}
        <div style={{ backgroundColor: '#333333', padding: '15px 20px', borderRadius: '12px' }}>
          <img
            src="https://www.ethosvillage.com/images/logo.svg"
            alt="Ethos Village Logo"
            style={{ height: '60px' }}
          />
        </div>

        {/* Welcome Text */}
        <p style={{ fontSize: '32px', color: '#333', marginTop: '10px', marginBottom: '0px' }}>
          Welcome to
        </p>

        {/* ProACTr Logo */}
        <img
          src="/proactr_logo_dark.png"
          alt="ProACTr Logo"
          style={{ width: '350px', marginTop: '5px' }}
        />
      </div>

      {/* Title and Subtitle */}
      <h2 style={styles.subtitle}>Learn by Doing</h2>

      {/* Registration Toggle Text */}
      <p
        onClick={() => {
          setShowRegistration(prev => !prev);
          setError('');
          setRegMessage('');
        }}
        style={{
          marginTop: '30px',
          fontSize: '20px',
          color: '#704ea0',
          textDecoration: 'underline',
          cursor: 'pointer'
        }}
      >
        {showRegistration
          ? 'Already have an account? Click here to return to login.'
          : 'First time here or not yet registered with ProACTr? Click here to register.'}
      </p>

      {/* Login form */}
      {!showRegistration ? (
        <div style={styles.formContainer}>
          <h1 style={styles.title}>Login</h1>
          <div style={{ width: '300px', marginTop: '5px' }}>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              style={styles.input}
            />
          </div>
          <div style={{ position: 'relative', width: '300px' }}>
            <input
              type={isConfirmPasswordVisible ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              style={styles.input}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleLogin(); // Call handleLogin when Enter is pressed
                }
              }}
            />
            <span
              onClick={() => setIsConfirmPasswordVisible(!isConfirmPasswordVisible)}
              style={styles.eyeIcon}
            >
              {isConfirmPasswordVisible ? eyeIcons.open : eyeIcons.closed}
            </span>
          </div>
          <button onClick={handleLogin} style={styles.button}>
            Login
          </button>

          {/* Forgot Password Link */}
          <p style={styles.forgotPassword}>
            Did you forget your password?{' '}
            <a href="/forgot-password" style={styles.link}>
              Click here
            </a>
          </p>

          {error && <p style={styles.error}>{error}</p>}

        </div>
      ) : (
        <div style={styles.formContainer}>
          <h2 style={{ fontSize: '18px', color: '#333', marginTop: '10px', width: '320px' }}>
            Register with your Ethos Village email:
          </h2>

          <input
            type="text"
            placeholder="First Name"
            value={regFirstName}
            onChange={(e) => setRegFirstName(e.target.value)}
            style={styles.input}
          />
          <input
            type="text"
            placeholder="Last Name"
            value={regLastName}
            onChange={(e) => setRegLastName(e.target.value)}
            style={styles.input}
          />
          <input
            type="email"
            placeholder="Email"
            value={regEmail}
            onChange={(e) => setRegEmail(e.target.value)}
            style={styles.input}
          />
          <button
            onClick={handleRegister}
            style={{ ...styles.button, opacity: isRegistering ? 0.6 : 1, cursor: isRegistering ? 'not-allowed' : 'pointer' }}
            disabled={isRegistering}
          >
            {isRegistering ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="loader" style={{
                  border: '3px solid #f3f3f3',
                  borderTop: '3px solid #fff',
                  borderRadius: '50%',
                  width: '16px',
                  height: '16px',
                  animation: 'spin 1s linear infinite',
                  marginRight: '8px'
                }} />
                Processing...
              </div>
            ) : 'Register'}
          </button>

          {regMessage && (
            <p style={{ ...styles.error, color: regMessageType === 'success' ? 'green' : 'red' }}>
              {regMessage}
            </p>
          )}
        </div>
      )}

    </div>
  );
}

// CSS in JS styles
const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    //justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    fontFamily: '"Open Sans", Arial, sans-serif',
    backgroundColor: '#f9f9f9',
    position: 'relative',
    marginTop: '-70px',
  },
  logo: {
    width: '500px',
    marginTop: '100px', // Set distance from the top of the page for the logo
  },
  title: {
    fontSize: '30px',
    fontWeight: 'bold',
    color: '#333',
    marginTop: '20px', // Set space below the logo
  },
  subtitle: {
    fontSize: '25px',
    fontStyle: 'italic',
    color: '#666',
    marginTop: '0px', // Set space below the title
  },
  formContainer: {
    width: '300px',
    textAlign: 'center',
    marginTop: '10px', // Set space below the subtitle for the form
  },
  loginTitle: {
    fontSize: '24px',
    marginTop: '10px',
    color: '#333',
  },
  input: {
    width: '275px',
    padding: '10px',
    marginTop: '5px',
    fontSize: '16px',
    borderRadius: '5px',
    border: '1px solid #ccc',
  },
  button: {
    width: '70%',
    padding: '10px',
    backgroundColor: '#704ea0',
    color: '#fff',
    fontSize: '18px',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    marginTop: '10px',
  },
  error: {
    color: 'red',
    marginTop: '20px',
  },
  registerText: {
    marginTop: '15px',
    fontSize: '14px',
  },
  forgotPassword: {
    marginTop: '15px',
    fontSize: '14px',
    textAlign: 'center',
    color: '#333',
  },
  link: {
    color: '#704ea0',
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  eyeIcon: {
    position: "absolute", // Position the icon inside the input
    right: "20px", // Place it near the right edge
    transform: "translateY(18px)",
    cursor: "pointer",
    fontSize: "16px",
    color: "rgba(0, 0, 0, 0.6)", // Black icon for simplicity
  },
};


export default Login;
